import {
  DailyPnlItem,
  IdeaHistoryDef,
} from '../../../../../apis/idea/idea-api.def.ts'
import { IdeyaAgGridTable } from '../../../../../common/ag-grid/ideya-ag-grid-table.tsx'
import { useTranslation } from 'react-i18next'
import { ColDef } from 'ag-grid-community'
import { useMemo } from 'react'
import {
  currencyValueFormatter,
  marketDateTimezoneFormatter,
} from '../../../../../utils/formatters.ts'
import { CustomCellRendererProps } from 'ag-grid-react'
import { IdeaDirectionTag } from '../../idea-list-table/idea-direction-tag.tsx'
import dayjs from 'dayjs'
import { IdeaActionLogItem } from './idea-action-log-table.def.ts'

interface IdeaActionLogTableProps {
  actionLog: IdeaHistoryDef
  dailyPnlData: DailyPnlItem[]
}

export const IdeaActionLogTable: React.FC<IdeaActionLogTableProps> = ({
  actionLog,
  dailyPnlData,
}) => {
  const { t } = useTranslation()

  // Calculating row data
  let rowData: IdeaActionLogItem[] = useMemo(() => {
    return actionLog.actions.map((item) => {
      // Find matching daily PnL data for this date
      const matchingPnl = dailyPnlData.find((pnl) =>
        dayjs(pnl.positionDate).isSame(dayjs(item.sendingTime), 'day')
      )

      return {
        date: item.sendingTime,
        action: item.actionType,
        direction: actionLog.idea.direction,
        targetMarketValue: item.targetMarketValue,
        size: null,
        qty: null,
        price: null,
        target: item.target,
        stop: item.stop,
        fxRate: null,
        currency: actionLog.idea.tickerCurrency,
        state: item.state,
        conviction: item.conviction,
        horizon: item.horizon,
        tradeReason: item.tradeReason,
        fillStrategy: item.fillStrategy,
        transactionCostLocal: null,
        transactionCostUsd: null,
        liquidateReason: actionLog.idea.liquidateReason,
        borrowFee: null,
        marketTimeZone: actionLog.idea.marketTimeZone,
        overallCost: null,
        pnl: matchingPnl?.pnl || null,
        realizedPnl: matchingPnl?.realizedPnl || null,
        ideaExecutionId: null,
      }
    })
  }, [
    dailyPnlData,
    actionLog.actions,
    actionLog.idea.direction,
    actionLog.idea.liquidateReason,
    actionLog.idea.marketTimeZone,
    actionLog.idea.tickerCurrency,
  ])

  for (const execution of actionLog.executions) {
    const tmpItem: IdeaActionLogItem = {
      date: execution.fillTime,
      action: execution.action,
      direction: actionLog.idea.direction,
      qty: execution.qty,
      price: execution.price,
      target: null,
      targetMarketValue: null,
      marketTimeZone: actionLog.idea.marketTimeZone,
      size: execution.size,
      stop: null,
      state: 'Filled',
      conviction: null,
      horizon: null,
      tradeReason: null,
      fxRate: execution.fxRate,
      currency: execution.currency,
      fillStrategy: execution.fillStrategy,
      borrowFee: execution.borrowFee,
      liquidateReason: actionLog.idea.liquidateReason,
      transactionCostLocal: execution.transactionCostLocal,
      transactionCostUsd: execution.transactionCostUsd,
      overallCost: execution.overallCost,
      ideaExecutionId: execution.ideaExecutionId,
    }

    // Prevent duplicate by checking for existing item
    const exists = rowData.some(
      (item) => item.ideaExecutionId === execution.ideaExecutionId
    )
    if (!exists) {
      const index =
        rowData.findIndex((item) => item.date <= execution.fillTime) + 1
      if (index === rowData.length) {
        rowData.push(tmpItem)
      } else {
        rowData = [...rowData.slice(0, index), tmpItem, ...rowData.slice(index)]
      }
    }
  }
  rowData = rowData.sort((a, b) => {
    return a.date > b.date ? 1 : -1
  })

  const columns: ColDef[] = useMemo(
    () => [
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.date'
        ),
        field: 'date',
        valueFormatter: (params) =>
          marketDateTimezoneFormatter(params, { showTimezone: true }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.action'
        ),
        field: 'action',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.direction'
        ),
        field: 'direction',
        cellRenderer: (props: CustomCellRendererProps) => {
          if (props.value !== undefined) {
            return <IdeaDirectionTag direction={props.data.direction} />
          }
          return <></>
        },
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.target_market_value'
        ),
        field: 'targetMarketValue',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.size'
        ),
        field: 'size',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.quantity'
        ),
        field: 'qty',
        type: 'numberColumn',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.price'
        ),
        field: 'price',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: params.data.currency,
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.target'
        ),
        field: 'target',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: params.data.currency,
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.stop'
        ),
        field: 'stop',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: params.data.currency,
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.fx_rate'
        ),
        field: 'fxRate',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 2,
            currency: '',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.currency'
        ),
        field: 'currency',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.state'
        ),
        field: 'state',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.conviction'
        ),
        field: 'conviction',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.horizon'
        ),
        field: 'horizon',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.trade_reason'
        ),
        field: 'tradeReason',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.fill_strategy'
        ),
        field: 'fillStrategy',
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.overall_cost'
        ),
        field: 'overallCost',
        type: 'numberColumn',
        valueFormatter: (params) =>
          currencyValueFormatter(params, {
            decimal: 0,
            currency: 'USD',
          }),
      },
      {
        headerName: t(
          'idea_list.idea_modal.idea_action_log.table.columns.liquidate_reason'
        ),
        field: 'liquidateReason',
      },
    ],
    [t]
  )

  return (
    <IdeyaAgGridTable
      height={400}
      columns={columns}
      rowData={rowData}
      //domLayout={'autoHeight'}
    />
  )
}
